// Auth API
import api from './client';

export interface RegistrationData {
  email: string;
  password: string;
  password_confirm: string;
  first_name: string;
  last_name: string;
  company_name?: string;
}

export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login/', { email, password }),

  register: (data: RegistrationData) =>
    api.post('/auth/register/', data),

  // The caller may hand us the bearer token explicitly. AuthContext clears
  // TokenManager synchronously as part of signing out, and axios runs its
  // request interceptor on a microtask — so by the time the interceptor looked
  // the token up it was already gone and the call went out anonymous. Pinning
  // it on the config keeps the one request that must be authenticated
  // authenticated, no matter what the caller does next.
  logout: (token?: string) =>
    api.post('/auth/logout/', undefined,
      token ? { headers: { Authorization: `Token ${token}` } } : undefined),

  profile: () =>
    api.get('/auth/profile/'),

  updateProfile: (data: { first_name?: string; last_name?: string; phone?: string; job_title?: string }) =>
    api.put('/auth/profile/', data),

  status: () =>
    api.get('/auth/status/'),

  // Impersonation (Login As). viewCompanyId pins the post-impersonation
  // landing scope to the company the admin was inspecting (e.g. clicked
  // through from /suppliers/<id>) — without it the backend pins to the
  // target's home, which is wrong for multi-site users.
  // viewGroupId pins the landing scope at GROUP level when the admin
  // invoked Login As from a Supplier Group page (CR-5316). When supplied
  // and the target user has access to ≥1 company in that group, backend
  // sets current_view_level='GROUP' instead of 'COMPANY'.
  impersonate: (
    userId?: number,
    userEmail?: string,
    viewCompanyId?: number,
    viewGroupId?: number,
  ) =>
    api.post('/auth/impersonate/', {
      user_id: userId,
      user_email: userEmail,
      view_company_id: viewCompanyId,
      view_group_id: viewGroupId,
    }),

  stopImpersonate: (originalToken: string, auditLogId?: number) =>
    api.post('/auth/stop-impersonate/', {
      original_token: originalToken,
      ...(auditLogId ? { audit_log_id: auditLogId } : {}),
    }),

  getImpersonatableUsers: () =>
    api.get('/auth/impersonatable-users/'),

  // Password reset
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password/', { email }),

  validateResetToken: (token: string) =>
    api.get(`/auth/validate-reset-token/?token=${token}`),

  resetPassword: (data: { token: string; new_password: string; confirm_password: string }) =>
    api.post('/auth/reset-password/', data),

  // CR-5275: TOTP MFA flow. The login response carries either
  //   { token, user }                                  — no MFA required
  //   { mfa_setup_required: true, mfa_session_token }  — first-time enrolment
  //   { mfa_required: true, mfa_session_token }        — code prompt only
  // The two helpers below close out the second and third branches.
  mfaSetupStart: (sessionToken: string) =>
    api.post('/auth/mfa/setup/', { mfa_session_token: sessionToken }),

  mfaSetupConfirm: (sessionToken: string, code: string) =>
    api.post('/auth/mfa/setup/confirm/', { mfa_session_token: sessionToken, code }),

  loginVerifyMfa: (sessionToken: string, code: string) =>
    api.post('/auth/login/verify-mfa/', { mfa_session_token: sessionToken, code }),
};
